/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { LogService, UtilsService } from '../../../../../../core/services';
import { Subject } from 'rxjs';
import { TemplateModel, TemplateOptions } from '../../../models';
import { WizardTitleEnum } from '../../../enums';

@Injectable()
export class WizardTemplateService {
    /**
     * Tells all components the template has changed.
     */
    public templateChangeEmitter: Subject<any> = new Subject<any>();

    constructor(
        private logService: LogService,
        private utilsService: UtilsService,
    ) {
    }

    /**
     * Pass in a template and a service you want to convert, returns service in template format with hidden and value fields.
     * @param template
     * @param serviceType
     * @param service
     */
    public convertServiceToTemplate(template: TemplateModel, serviceType: string, service: object): any {
        if (!template.sections[serviceType] && !template.hiddenSections[serviceType]) {
            this.logService.error('WizardTemplateService:convertServiceToTemplate', 'Template missing required section');
        } else {
            const sections: string = template.sections[serviceType] ? 'sections' : 'hiddenSections';
            const templateSection: any = this.utilsService.duplicateObject(template[sections] && template[sections][serviceType][0]);

            return this.assignServiceValuesToTemplate(service, templateSection);
        }
    }

    /**
     * Convert template to service
     * @param serviceData
     */
    public convertTemplateToService(serviceData: any): any {
        const serviceValues = {};
        for (const property in serviceData) {
            if (serviceData.hasOwnProperty(property) &&
                (serviceData[property].value !== undefined && serviceData[property].value !== null)) {
                serviceValues[property] = serviceData[property].value;
            } else {
                // Remove properties with null values.
                delete serviceValues[property];
            }
        }

        return serviceValues;
    }

    /**
     * Change the visible inputs in the template to only show the ones required for duplication.
     * @param template
     * @param serviceType
     * @param inputsToShow
     * @param inputsToHide
     * @param inputsToDelete
     */
    public updateTemplatesInputs(
        template: TemplateModel,
        serviceType: string,
        inputsToShow: string[],
        inputsToHide: string[],
        inputsToDelete: string[]
    ): void {
        const services = template.sections[serviceType];
        for (const service of services) {
            this.updateServiceInputs(service, inputsToShow, inputsToHide, inputsToDelete);
        }
    }

    /**
     * Change the visible inputs in the template section to only show the required ones.
     * @param service
     * @param inputsToShow
     * @param inputsToHide
     * @param inputsToDelete
     */
    public updateServiceInputs(service: any, inputsToShow: string[] = [], inputsToHide: string[] = [], inputsToDelete: string[] = []): any {
        const arraysShareValues: boolean = this.utilsService.compareArrays(inputsToShow, inputsToHide);
        if (arraysShareValues) {
            this.logService.warn('WizardTemplateService:updateServiceInputs', 'Some inputs are set to both show and hide');
        }

        for (const property in service) {
            if (service.hasOwnProperty(property) && property !== 'title' && property !== 'subSteps') {
                if (inputsToShow.includes('all')) {
                    service[property].hidden = false;
                }

                if (inputsToHide.includes('all')) {
                    service[property].hidden = true;
                }

                if (inputsToShow.includes(property)) {
                    service[property].hidden = false;
                }

                if (inputsToHide.includes(property)) {
                    service[property].hidden = true;
                }

                if (inputsToDelete.includes(property)) {
                    delete service[property];
                }
            }
        }

        service['forceTemplateView'] = !!inputsToShow.length;
    }

    /**
     * Validate the selected template with set properties.
     * @param selectedTemplate
     */
    public validateSelectedTemplate(selectedTemplate: TemplateModel): any[] {
        if (selectedTemplate) {
            const checkProperties = {
                required: {
                    title: 'string',
                    version: 'number',
                    type: 'string',
                    modes: 'array',
                    sections: 'object'
                },
                optional: {
                    htmlId: 'string',
                    builds: 'object',
                    backendReferences: 'array',
                    description: ['string', 'object'],
                    disabled: 'boolean',
                    hideMultipleServices: 'boolean',
                    fipsReference: 'array',
                    advancedMode: 'boolean',
                    wizardDefaultMode: 'string',
                    wizardModes: 'array',
                    wizardHideModes: 'boolean',
                    wizardStartPosition: ['string', 'number', 'array'],
                    // User template fix
                    isUserTemplate: 'boolean',
                    fileName: 'string',
                    hiddenSections: 'object'
                }
            };

            return this.validationConditions(checkProperties, selectedTemplate);
        }
    }

    /**
     * Pass in an array of templates and title, returns a cloned version of the template.
     * @param templates
     * @param serviceType
     */
    public getClonedTemplate(templates: TemplateModel[], serviceType?: string): any {
        if (templates.length === 1 && !serviceType) {
            return this.utilsService.duplicateObject(templates[0]);
        }

        if (templates.length >= 1 && !serviceType) {
            this.logService.error(
                'getClonedTemplate',
                'You have supplied multiple template but no type to match on, first template returned.'
            );

            return this.utilsService.duplicateObject(templates[0]);
        }

        const template: TemplateModel = templates.length === 1 ? templates[0] : templates.find(item => item.type === serviceType);

        return this.utilsService.duplicateObject(template);
    }

    /**
     * Takes the count of the reals in the sections part of the template and times it by the number of real servers to configure.
     * @param template
     * @private
     */
    public getRealsCount(template: TemplateModel): number {
        const realsCounter = this.utilsService.getObjectValue(
            template,
            `builds.${WizardTitleEnum.REALSERVERS}.0.count.value`
        ) || 1;

        return template.sections[WizardTitleEnum.REALSERVERS].length * realsCounter;
    }

    /**
     * Take in a template and formats the description by replacing service placeholders with counts of the service.
     * @param template
     * @param step
     * @private
     */
    public formatTemplateDescription(template: TemplateModel, step: any): any {
        if (template.description) {
            const serviceCount = this.getServicesCount(template);
            const templateDescription = template.description;
            const description = typeof templateDescription === 'string'
                ? templateDescription : templateDescription[step.service];

            return this.replacePlaceholderInDescription(serviceCount, description);
        }
    }

    /**
     * Create service from template.
     * @param serviceTemplate
     * @param WizardTitleEnum
     * @param serviceObject
     * @param options
     * @private
     */
    public createServiceFromTemplate(
        serviceTemplate: TemplateModel,
        WizardTitleEnum: string,
        serviceObject: object,
        options: TemplateOptions = {}
    ): any {
        const service: any = this.convertServiceToTemplate(
            serviceTemplate, WizardTitleEnum, serviceObject
        );

        if (Object.keys(options)?.length) {
            const { inputsToShow, inputsToHide, inputsToDelete } = options;
            if (inputsToShow || inputsToHide || inputsToDelete) {
                this.updateServiceInputs(service, inputsToShow, inputsToHide, inputsToDelete);
            }
        } else {
            this.logService.debug('WizardUtilsService:createServiceFromTemplate', 'No options set for ' + WizardTitleEnum);
        }

        return service;
    }

    /**
     * Validate required key, extra keys and key data types.
     * @param checkProperties
     * @param selectedTemplate
     * @private
     */
    private validationConditions(checkProperties: any, selectedTemplate: TemplateModel): any[] {
        const errors = [];

        const requiredKeys = Object.keys(checkProperties.required);
        const optionalKeys = Object.keys(checkProperties.optional);

        if (selectedTemplate['fileName'] && selectedTemplate['type'] === 'monitor') {
            const missingReferences = this.getMissingReferences(selectedTemplate);
            if (missingReferences.length) {
                errors.push(this.formatArrayMessage(missingReferences, 'missing'));
            }
        }

        // Check for missing keys
        const missingKeys = this.checkRequiredKeys(selectedTemplate, requiredKeys);
        if (missingKeys.length) {
            errors.push(this.formatArrayMessage(missingKeys, 'missing'));
        }

        // Check for keys that shouldn't be there
        const extraKeys = this.checkForExtraKeys(selectedTemplate, requiredKeys, optionalKeys);
        if (extraKeys.length) {
            errors.push(this.formatArrayMessage(extraKeys, 'not allowed'));
        }

        // Check for incorrect data types
        const incorrectDataTypes = this.checkTemplateDataTypes(checkProperties, selectedTemplate, extraKeys);
        if (incorrectDataTypes.length) {
            errors.push(...incorrectDataTypes);
        }

        // Check for section counts are correct
        const incorrectSectionsCount = this.checkTemplateSectionCounts(selectedTemplate);
        if (incorrectSectionsCount.length) {
            errors.push(...incorrectSectionsCount);
        }

        return errors;
    }

    /**
     * Get missing references from template.
     * @param selectedTemplate
     * @private
     */
    private getMissingReferences(selectedTemplate: TemplateModel) {
        const references = ['fipsReference', 'backendReferences'];

        return references.filter(
            reference => !Object.keys(selectedTemplate).includes(reference)
        );
    }

    /**
     * Check for missing required keys
     * @param selectedTemplate
     * @param requiredKeys
     * @private
     */
    private checkRequiredKeys(selectedTemplate: TemplateModel, requiredKeys: any[]): string[] {
        return requiredKeys.filter(
            key => !Object.keys(selectedTemplate).includes(key)
        );
    }

    /**
     * Check for keys that are not allowed.
     * @param selectedTemplate
     * @param requiredKeys
     * @param optionalKeys
     * @private
     */
    private checkForExtraKeys(selectedTemplate: TemplateModel, requiredKeys: string | string[], optionalKeys: string | string[]): string[] {
        return Object.keys(selectedTemplate).filter(
            key => !requiredKeys.includes(key) && !optionalKeys.includes(key)
        );
    }

    /**
     * Format message with the correct Key(s) and is/are with condition.
     * @param keys
     * @param condition
     * @private
     */
    private formatArrayMessage(keys: any[], condition: string): string {
        if (keys.length > 1) {
            const keysList = keys.join(', ');

            return `Keys <strong>${this.utilsService.replaceLastCommaWithAnd(keysList)}</strong> are ${condition} in this template`;
        } else {
            return `Key <strong>${keys[0]}</strong> is ${condition} in this template`;
        }
    }

    /**
     * Check the data type for each property in the template
     * @param checkProperties
     * @param selectedTemplate
     * @param extraKeys
     * @private
     */
    private checkTemplateDataTypes(checkProperties: any, selectedTemplate: TemplateModel, extraKeys: string[]): string[] {
        const properties = Object.assign(checkProperties.required, checkProperties.optional);
        const errors: any[] = [];

        for (const key in selectedTemplate) {
            if (selectedTemplate.hasOwnProperty(key) && !extraKeys.includes(key)) {
                const templateValue = selectedTemplate[key];

                if (properties[key] === 'array' || properties[key].includes('array') || Array.isArray(properties[key])) {
                    const arrayError = this.handleArrays(properties, templateValue, key);
                    if (arrayError) {
                        errors.push(arrayError);
                    }
                } else {
                    if (properties[key] !== this.utilsService.typeOfIncludingArray(templateValue)) {
                        const errorMessage = this.getFormattedMessage(key, templateValue, properties[key]);
                        if (errorMessage) {
                            errors.push(errorMessage);
                        }
                    }
                }
            }
        }

        return errors;
    }

    /**
     * Handle arrays
     * @param properties
     * @param templateValue
     * @param key
     * @private
     */
    private handleArrays(properties: any, templateValue: any, key: string): string {
        if (this.hasEmptyValues(templateValue)) {
            return `The <strong>${key}</strong> array is empty`;
        } else if (properties[key] && !properties[key].includes(this.utilsService.typeOfIncludingArray(templateValue))) {
            const keys = Array.isArray(properties[key]) ? properties[key].join(', ') : properties[key];
            const typeList = this.utilsService.replaceLastCommaWithOr(keys);

            return this.getFormattedMessage(key, templateValue, typeList);
        } else {
            this.logService.debug(
                'WizardTemplateService: checkTemplateDataTypes',
                'Is an array but no error found'
            );
        }
    }

    /**
     * Check for empty array and/or empty values.
     * @param templateValue
     * @private
     */
    private hasEmptyValues(templateValue: any): boolean {
        if (typeof templateValue === 'string') {
            return !templateValue.length;
        }

        if (Array.isArray(templateValue)) {
            if (!templateValue.length) {
                return true;
            } else {
                return templateValue.some(
                    (item: any): boolean => typeof item === 'string' && !item?.length || item === undefined || item === ''
                );
            }
        }

        return false;
    }

    /**
     * Get formatted message.
     * @param key
     * @param templateValue
     * @param typeList
     * @private
     */
    private getFormattedMessage(key: string, templateValue: any, typeList: any): string {
        return `The data type for <strong>${key}</strong> is incorrect.<br>
                It is of type <strong>${this.getValueType(templateValue)}</strong> and should be of type
                <strong>${typeList}</strong>`;
    }

    /**
     * Filter type array.
     * @param templateValue
     * @private
     */
    private getValueType(templateValue: any): string {
        if (Array.isArray(templateValue)) {
            return 'array';
        } else {
            return typeof templateValue;
        }
    }

    /**
     * Check template sections
     * @param selectedTemplate
     * @private
     */
    private checkTemplateSectionCounts(selectedTemplate: TemplateModel): string[] {
        const errors = [];
        const sections = selectedTemplate.sections;
        const services = {};

        for (const property in sections) {
            if (sections.hasOwnProperty(property)) {
                services[property] = sections[property].length;
            }
        }

        if (services[WizardTitleEnum.FRONTENDS] > services[WizardTitleEnum.BACKENDS]) {
            errors.push('There are more "Frontend" sections than "Backend" sections');
        }

        if (services[WizardTitleEnum.REALSERVERS] < services[WizardTitleEnum.BACKENDS]) {
            errors.push('There are less "Real server" sections than "Backend" sections');
        }

        if (services[WizardTitleEnum.ACLS] > services[WizardTitleEnum.BACKENDS]) {
            errors.push('There are more "Acl" sections than "Backend" sections');
        }

        return errors;
    }

    /**
     * Takes service property values and assigns them to the template. If a value is missing that property will be hidden.
     * @param service
     * @param templateSection
     * @private
     */
    private assignServiceValuesToTemplate(service: object, templateSection: any): object {
        const responseTemplate: any = templateSection;
        for (const property in service) {
            if (service.hasOwnProperty(property)) {
                if (responseTemplate[property]) {
                    responseTemplate[property].value = service[property];
                } else {
                    responseTemplate[property] = {
                        value: service[property],
                        hidden: true
                    };
                }
            }
        }

        return responseTemplate;
    }

    /**
     * Replace each placeholder with the service count.
     * @param serviceCount
     * @param description
     * @private
     */
    private replacePlaceholderInDescription(serviceCount: any, description: string): string {
        for (const service in serviceCount) {
            if (serviceCount.hasOwnProperty(service)) {
                const replace = `{{${service}}}`;
                if (description && description.includes(replace)) {
                    description = description.replace(replace, serviceCount[service]);
                }
            }
        }

        return description;
    }

    /**
     * Get the number of services in the sections part of the template.
     * @param template
     * @private
     */
    private getServicesCount(template: TemplateModel): any {
        const serviceCount = {};
        for (const builds in template.sections) {
            if (template.sections.hasOwnProperty(builds)) {
                serviceCount[builds] = template.sections[builds].length;
                if (builds === WizardTitleEnum.REALSERVERS) {
                    serviceCount['totalReals'] = this.getRealsCount(template);
                }
            }
        }

        return serviceCount;
    }
}
